// Builds a YOLO Kid in the browser from the shared voxel library (public/vx) and the token's
// recipe from /api/vx/:id, and plays the shared VoxEdit animations. Replaces the per-token glTF.
import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';
import { FPS, decodePart, compositeLayers, buildFaces, samplePose, newPose } from '../vx/lib';

const BLEND_TIME = 0.5;
const DEFAULT_ANIM = 'Wave 01';

// ---------- shared library (fetched once per page) ----------
let libraryPromise = null;
function loadLibrary() {
  if (!libraryPromise) {
    const j = f => fetch(`/vx/${f}`).then(r => { if (!r.ok) throw new Error(`${f}: HTTP ${r.status}`); return r.json(); });
    libraryPromise = Promise.all([
      j('rig.json'), j('anims.json'), j('parts.json'),
      fetch('/vx/parts.bin').then(r => { if (!r.ok) throw new Error(`parts.bin: HTTP ${r.status}`); return r.arrayBuffer(); }).then(b => new Uint8Array(b)),
    ]).then(([rig, anims, partMeta, partsBin]) => ({ rig, anims, partMeta, partsBin, decoded: new Map() }));
  }
  return libraryPromise;
}

const toLinear = new Float32Array(256).map((_, i) => { const c = i / 255; return c < 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4; });
const litMat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 1, metalness: 0 });
const glowMat = new THREE.MeshBasicMaterial({ vertexColors: true });

function part(lib, i) {
  if (!lib.decoded.has(i)) lib.decoded.set(i, { meta: lib.partMeta[i], voxels: decodePart(lib.partMeta[i], lib.partsBin) });
  return lib.decoded.get(i);
}

function geometryFor(buf) {
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(buf.pos, 3));
  geo.setAttribute('normal', new THREE.Float32BufferAttribute(buf.nrm, 3));
  const col = new Float32Array(buf.col.length);
  for (let i = 0; i < col.length; i++) col[i] = toLinear[Math.round(buf.col[i] * 255)];
  geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
  return geo;
}

// Same node layout as the VoxEdit glTF export: Root (mirror) > rig nodes; meshes sit at voxel - pivot.
function buildCharacter(lib, layers) {
  const group = new THREE.Group();
  const root = new THREE.Group();
  root.quaternion.set(0, -1, 0, 0);
  root.scale.set(-1, 1, 1);
  group.add(root);
  const rigObjects = lib.rig.nodes.map(n => { const g = new THREE.Group(); g.name = n.name; return g; });
  lib.rig.nodes.forEach((n, i) => (n.parent >= 0 ? rigObjects[n.parent] : root).add(rigObjects[i]));
  const meshNodes = lib.rig.nodes.map((n, i) => (n.mesh ? i : -1)).filter(i => i >= 0);
  const geometries = [];
  layers.forEach((layerIds, m) => {
    const comp = compositeLayers(layerIds.map(i => part(lib, i)));
    if (!comp) return;
    const { lit, glow } = buildFaces(comp);
    for (const [buf, mat] of [[lit, litMat], [glow, glowMat]]) {
      if (!buf.pos.length) continue;
      const geo = geometryFor(buf);
      geometries.push(geo);
      const mesh = new THREE.Mesh(geo, mat);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      rigObjects[meshNodes[m]].add(mesh);
    }
  });
  return { group, rigObjects, dispose: () => geometries.forEach(g => g.dispose()) };
}

export default function ModelVoxel({ number, animation }) {
  const [model, setModel] = useState(null);
  const anim = useRef({ cur: null, prev: null, blendT: 1, time: 0, lib: null });
  const poseA = useRef(newPose()).current;
  const poseB = useRef(newPose()).current;
  const tmp = useRef({ pa: new THREE.Vector3(), pb: new THREE.Vector3(), qa: new THREE.Quaternion(), qb: new THREE.Quaternion() }).current;

  useEffect(() => {
    let cancelled = false, built = null;
    (async () => {
      try {
        const [lib, res] = await Promise.all([loadLibrary(), fetch(`/api/vx/${number}`)]);
        if (!res.ok) throw new Error(`token ${number}: ${res.status}`);
        const recipe = await res.json();
        if (cancelled) return;
        built = buildCharacter(lib, recipe.layers);
        anim.current.lib = lib;
        setModel(built);
      } catch (e) {
        console.error('Error building YOLO voxel model:', e);
      }
    })();
    return () => { cancelled = true; if (built) built.dispose(); setModel(null); };
  }, [number]);

  // switch animation with a crossfade from the frozen previous pose
  useEffect(() => {
    const a = anim.current;
    if (!a.lib) return;
    const name = a.lib.anims[animation] ? animation : DEFAULT_ANIM;
    if (a.cur && a.cur.name === name) return;
    if (a.cur) a.prev = { name: a.cur.name, frame: frameOf(a, a.cur) };
    a.blendT = a.cur ? 0 : 1;
    a.cur = { name, startTime: a.time };
  }, [animation, model]);

  useFrame((_, delta) => {
    const a = anim.current;
    if (!model || !a.lib || !a.cur) return;
    const dt = Math.min(delta, 0.1);
    a.time += dt;
    a.blendT = Math.min(1, a.blendT + dt / BLEND_TIME);
    const curAnim = a.lib.anims[a.cur.name], curFrame = frameOf(a, a.cur);
    const prevAnim = a.blendT < 1 && a.prev ? a.lib.anims[a.prev.name] : null;
    const w = a.blendT < 1 ? a.blendT * a.blendT * (3 - 2 * a.blendT) : 1;
    model.rigObjects.forEach((obj, i) => {
      samplePose(curAnim, i, curFrame, poseA);
      tmp.pa.fromArray(poseA.p); tmp.qa.fromArray(poseA.q);
      let s = poseA.s;
      if (prevAnim && w < 1) {
        samplePose(prevAnim, i, a.prev.frame, poseB);
        tmp.pb.fromArray(poseB.p).lerp(tmp.pa, w); tmp.pa.copy(tmp.pb);
        tmp.qb.fromArray(poseB.q).slerp(tmp.qa, w); tmp.qa.copy(tmp.qb);
        s = poseB.s + (s - poseB.s) * w;
      }
      obj.position.copy(tmp.pa);
      obj.quaternion.copy(tmp.qa);
      obj.scale.setScalar(s || 1);
    });
  });

  return model && (
    <primitive object={model.group} scale={0.04} position={[0, -1, 0]} rotation={[0, 3.5, 0]} />
  );
}

function frameOf(a, state) {
  const maxFrame = a.lib.anims[state.name].maxFrame;
  return maxFrame <= 0 ? 0 : ((a.time - state.startTime) * FPS) % maxFrame;
}
